import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { take, takeUntil } from "rxjs/operators";
import { Subject } from "../../_objects/subject";
import { SubjectModelListItem } from "../../_objects/subject-model";
import { FeatureFlagService } from "../../_services/feature-flag/feature-flag.service";
import { SubjectModelService } from "../../_services/http-api/subject-model.service";
import { TenantConfigService } from "../../_services/http-api/tenant-config.service";
import LiveAssetsStateService from "../../_services/live-assets-state.service";
import { SearchSelectOption } from "../../_shared/component/search-combo-box/search-select-option";
import { ComboBoxOption, FilterData, FilterSelectDialogComponent } from "../../_shared/dialog/filter-select-dialog/filter-select-dialog.component";
import { FEATURE_FLAGS, FILTER_CONTENT_ID, INSPECTION_STATUS, TenantConfig } from "../../app.constants";

export type InspectionListSort = { value: null | "inspectionDate" | "title"; direction: null | "asc" | "desc" };

@Component({
  selector: "app-inspection-list-filter",
  templateUrl: "./inspection-list-filter.component.html",
  styleUrl: "./inspection-list-filter.component.scss",
  standalone: false
})
export class InspectionListFilterComponent implements OnInit {
  public filterData: FilterData[] = [];
  public sortData: InspectionListSort = { value: "inspectionDate", direction: "desc" };

  @Input() disableFilters: boolean = false;

  @Output() public filtersChanged: EventEmitter<FilterData[]> = new EventEmitter();
  @Output() public sortChanged: EventEmitter<InspectionListSort> = new EventEmitter();

  private subjectsModel: SubjectModelListItem[] = [];
  private serialNumberLabel = TenantConfig.DefaultConfigurationValues.LabelOfSerialNumber;
  private isAssetModuleTenant: () => boolean;

  constructor(
    private subjectModelService: SubjectModelService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private tenantConfigService: TenantConfigService,
    private featureFlagService: FeatureFlagService,
    private liveAssetsStateService: LiveAssetsStateService
  ) {
    this.isAssetModuleTenant = this.featureFlagService.isFlagEnabled(FEATURE_FLAGS.ASSET_MODULE);
  }

  ngOnInit(): void {
    this.getSubjectsModelData();
    this.setConfig();
  }

  public get isTitleAsc() {
    return this.sortData.value == "title" && this.sortData.direction == "asc";
  }

  public get isDateAsc() {
    return this.sortData.value == "inspectionDate" && this.sortData.direction == "asc";
  }

  public changeSortByDate(direction: "asc" | "desc") {
    this.sortData = { value: "inspectionDate", direction };
    this.sortChanged.emit(this.sortData);
  }

  public changeSortByTitle(direction: "asc" | "desc") {
    this.sortData = { value: "title", direction };
    this.sortChanged.emit(this.sortData);
  }

  private setConfig() {
    this.tenantConfigService
      .getTenantConfigs()
      .pipe(take(1))
      .subscribe((configs) => {
        if (configs) {
          const snLabelConfig = configs.find((_) => _.key === TenantConfig.LabelOfSerialNumber.KEY);
          if (snLabelConfig) {
            this.serialNumberLabel = snLabelConfig.value as string;
          }
        }
      });
  }

  private getSubjectsModelData() {
    this.subjectModelService.getSubjectModels().subscribe((val) => (this.subjectsModel = val));
  }

  private getSelectedIdFor(id: string) {
    return this.filterData.find((filter) => filter.id == id)?.value;
  }

  public getFiltersOptions() {
    const defaultOpt = new SearchSelectOption(null, this.translate.instant("inspectionlist.all"));
    const isAssetModule = this.isAssetModuleTenant();

    const statusOptions = Array.from(INSPECTION_STATUS.keys())
      .sort()
      .map((st) => new SearchSelectOption(st, this.translate.instant("inspectionlist.status_" + st.toString().toLowerCase().replace(" ", ""))));

    const filters: { id: string; label: string; options: SearchSelectOption[]; selected: string | boolean; isCheckbox?: boolean }[] = [
      {
        id: FILTER_CONTENT_ID.STATUS,
        label: this.translate.instant("inspectionlist.status"),
        options: [defaultOpt, ...statusOptions],
        selected: this.getSelectedIdFor(FILTER_CONTENT_ID.STATUS)
      },
      {
        id: FILTER_CONTENT_ID.SUBJECT_MODEL,
        label: isAssetModule ? this.translate.instant("inspectionlist.blueprint") : this.translate.instant("inspectionlist.model"),
        options: [defaultOpt, ...this.getSubjectModelOptions(isAssetModule)],
        selected: this.getSelectedIdFor(FILTER_CONTENT_ID.SUBJECT_MODEL)
      },
      {
        id: FILTER_CONTENT_ID.SUBJECT,
        label: isAssetModule ? this.translate.instant("inspectionlist.asset") : this.serialNumberLabel,
        options: [defaultOpt, ...this.getSubjectOptions(isAssetModule)],
        selected: this.getSelectedIdFor(FILTER_CONTENT_ID.SUBJECT)
      },
      {
        id: FILTER_CONTENT_ID.IS_OWN,
        label: this.translate.instant("inspectionlist.myInspections"),
        options: [],
        selected: this.getSelectedIdFor(FILTER_CONTENT_ID.IS_OWN),
        isCheckbox: true
      }
    ];

    if (!isAssetModule) {
      const generateOptions = (propertyName: keyof Subject) =>
        this.liveAssetsStateService
          .liveAssets()
          .filter((s) => !!s[propertyName])
          .map((s) => new SearchSelectOption(s[propertyName].toString(), s[propertyName].toString()))
          .reduce((acc, opt) => (acc.find((o) => o.label === opt.label) ? acc : [...acc, opt]), [] as SearchSelectOption[]);

      filters.push(
        {
          id: FILTER_CONTENT_ID.OWNER,
          label: this.translate.instant("inspectionlist.owner"),
          options: [defaultOpt, ...generateOptions("owner")],
          selected: this.getSelectedIdFor(FILTER_CONTENT_ID.OWNER)
        },
        {
          id: FILTER_CONTENT_ID.COUNTRY,
          label: this.translate.instant("inspectionlist.country"),
          options: [defaultOpt, ...generateOptions("country")],
          selected: this.getSelectedIdFor(FILTER_CONTENT_ID.COUNTRY)
        },
        {
          id: FILTER_CONTENT_ID.STATE,
          label: this.translate.instant("inspectionlist.state"),
          options: [defaultOpt, ...generateOptions("state")],
          selected: this.getSelectedIdFor(FILTER_CONTENT_ID.STATE)
        },
        {
          id: FILTER_CONTENT_ID.DISTRICT,
          label: this.translate.instant("inspectionlist.district"),
          options: [defaultOpt, ...generateOptions("district")],
          selected: this.getSelectedIdFor(FILTER_CONTENT_ID.DISTRICT)
        },
        {
          id: FILTER_CONTENT_ID.SITE,
          label: "Site",
          options: [defaultOpt, ...generateOptions("site")],
          selected: this.getSelectedIdFor(FILTER_CONTENT_ID.SITE)
        },
        {
          id: FILTER_CONTENT_ID.PROJECT,
          label: "Project",
          options: [defaultOpt, ...generateOptions("project")],
          selected: this.getSelectedIdFor(FILTER_CONTENT_ID.PROJECT)
        }
      );
    }

    return filters.map((f) => new ComboBoxOption(f.id, f.label, f.options, f.selected, f?.isCheckbox));
  }

  private getSubjectModelOptions(isAssetModule: boolean): SearchSelectOption[] {
    if (!isAssetModule) {
      return this.subjectsModel.map((m) => new SearchSelectOption(m.id, m.name));
    }
    return this.liveAssetsStateService.assetTypes().map((t) => new SearchSelectOption(t.id, t.name));
  }

  private getSubjectOptions(isAssetModule: boolean): SearchSelectOption[] {
    if (!isAssetModule) {
      return this.liveAssetsStateService.liveAssets().map((s) => new SearchSelectOption(s.id, s.serialNo));
    }
    return this.liveAssetsStateService.liveAssets().map((s) => new SearchSelectOption(s.id, s.serialNo ? `${s.name}_${s.serialNo}` : s.name));
  }

  openAddFilterDialog() {
    const dialogRef = this.dialog.open(FilterSelectDialogComponent, {
      width: "560px",
      panelClass: "dialog-scroll",
      data: {
        comboBoxOptions: this.getFiltersOptions()
      }
    });

    dialogRef.componentInstance.changedFilter.pipe(takeUntil(dialogRef.afterClosed())).subscribe((result) => {
      result = result.filter((f) => f.label !== undefined);
      this.filterData = result;
      this.filtersChanged.emit(result);
      dialogRef.close();
    });
  }

  clearFilter(filter: FilterData) {
    this.filterData = this.filterData.filter((f) => f.id !== filter.id);
    this.filtersChanged.emit(this.filterData);
  }
}
